export interface IPhotoCoordinates {
  latitude: number;
  longitude: number;
}

const parseCoordinate = (value: unknown): number | null => {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (typeof value === 'string' && !/^[+-]?(?:\d+\.?\d*|\.\d+)$/.test(value.trim())) return null;

  const parsed = typeof value === 'number' ? value : Number(value.trim());
  return Number.isFinite(parsed) ? parsed : null;
};

/** Reject missing/invalid values and the 0,0 sentinel before showing audit evidence. */
export const getValidPhotoCoordinates = (
  latitude: unknown,
  longitude: unknown,
): IPhotoCoordinates | null => {
  const lat = parseCoordinate(latitude);
  const lng = parseCoordinate(longitude);
  if (lat === null || lng === null || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;

  const coordinates = {
    latitude: Math.round(lat * 10000000) / 10000000,
    longitude: Math.round(lng * 10000000) / 10000000,
  };
  return coordinates.latitude === 0 && coordinates.longitude === 0 ? null : coordinates;
};

export const getPhotoGoogleMapsUrl = (coordinates: IPhotoCoordinates): string =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${coordinates.latitude},${coordinates.longitude}`)}`;

/** Great-circle distance; this is a comparison aid, not an arrival/geofence verdict. */
export const getPhotoBranchDistanceMeters = (
  photo: IPhotoCoordinates,
  branch: IPhotoCoordinates,
): number => {
  const radians = (degrees: number) => (degrees * Math.PI) / 180;
  const latitudeDelta = radians(branch.latitude - photo.latitude);
  const longitudeDelta = radians(branch.longitude - photo.longitude);
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(radians(photo.latitude)) * Math.cos(radians(branch.latitude)) * Math.sin(longitudeDelta / 2) ** 2;
  return 6371000 * 2 * Math.asin(Math.sqrt(Math.min(1, Math.max(0, haversine))));
};
